/**
 * 搜索结果卡片组件
 * 显示单个搜索结果
 */

import React, { useState, Component, ReactNode } from 'react';
import { ChevronDown, ChevronRight, ExternalLink, Search, AlertCircle, ImageIcon, Sparkles, Globe, Loader2 } from 'lucide-react';
import type { SearchResult, SavedSearchImage } from '@/types/agent';
import { getFaviconUrl, getDomainName } from './utils/searchUrlUtils';
import { ACTION_LABELS } from './config/searchResultConfig';
import { renderFormattedMessage } from './components/ProcessLogPanel';
import { ImageGallery } from './components/ImageGallery';

// 🔵 P2-1: 统一提取 searchData 到 SearchResultsList props 的 helper
// 消除 MessageList.tsx（2 处）和 MessageBubble.tsx（1 处）的 12 行重复属性解构
export const buildSearchListProps = (searchData: any, streamingSummary?: string): SearchResultsListProps => ({
  query: searchData.query || '',
  results: searchData.results || [],
  summary: searchData.error || searchData.summary || '',  // I1: 兜底空字符串，避免 undefined
  answer: searchData.answer,
  searchType: searchData.search_type,
  optimizedQuery: searchData.optimized_query,
  crawledPages: searchData.crawled_pages,
  suggestions: searchData.suggestions,
  savedImages: searchData.saved_images,
  visionSummary: searchData.vision_summary,
  rawSearchData: searchData._raw || searchData,  // P0: 降级数据通过 _fallback 标记传递
  warning: searchData.warning,
  searchLogs: searchData.search_logs,
  streamingSummary,
});

class SummaryErrorBoundaryInner extends Component<
  { fallback: string; children: ReactNode; resetKey?: string },
  { hasError: boolean }
> {
  state = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidUpdate(prevProps: { resetKey?: string }) {
    if (this.state.hasError && this.props.resetKey !== prevProps.resetKey) {
      this.setState({ hasError: false });
    }
  }
  render() {
    if (this.state.hasError) {
      // P1(v8): 降级时仍使用格式化渲染（skipIncomplete=true），
      // 而非纯文本。避免"纯文本→格式化"的视觉闪烁。
      // skipIncomplete=true 会将不完整 markdown 结构转回纯文本行，
      // 不会触发 renderFormattedMessageInner 的异常。
      return (
        <div className="text-[12px] text-gray-300 select-text cursor-text">
          {renderFormattedMessage(this.props.fallback || '', true)}
        </div>
      );
    }
    return this.props.children;
  }
}

const SummaryErrorBoundary: React.FC<{ fallback: string; children: ReactNode; resetKey?: string }> = ({ fallback, children, resetKey }) => (
  <SummaryErrorBoundaryInner fallback={fallback} resetKey={resetKey}>
    {children}
  </SummaryErrorBoundaryInner>
);

interface SearchResultCardProps {
  result: SearchResult;
  index: number;
}

/**
 * 搜索结果卡片组件
 * 显示单个搜索结果，包含标题、域名、摘要等
 */
export const SearchResultCard: React.FC<SearchResultCardProps> = ({ result, index }) => {
  const [expanded, setExpanded] = useState(false);
  const [faviconError, setFaviconError] = useState(false);
  
  // 使用工具函数提取 URL 信息
  const favicon = getFaviconUrl(result.url);
  const domain = getDomainName(result.url);
  const hasSnippet = !!result.snippet && result.snippet.length > 0;

  return (
    <div className="group">
      <a
        href={result.url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-start gap-2 px-2 py-1.5 rounded-md hover:bg-white/[0.04] transition-colors border border-transparent hover:border-blue-500/30"
      >
        {/* 序号 */}
        <span className="shrink-0 mt-0.5 w-4 h-4 rounded-sm bg-blue-500/20 text-blue-400 flex items-center justify-center text-[10px] font-medium">
          {index + 1}
        </span>

        {/* 内容 */}
        <div className="min-w-0 flex-1">
          {/* 域名行 */}
          <div className="flex items-center gap-1.5">
            {favicon && !faviconError && (
              <img 
                src={favicon} 
                alt="" 
                className="w-3.5 h-3.5 shrink-0 rounded-sm" 
                onError={() => setFaviconError(true)} 
              />
            )}
            <span className="text-[11px] text-gray-400 truncate">{domain}</span>
          </div>
          
          {/* 标题 + 低相关性警示（I7+8） */}
          <h4 className="text-[12px] text-blue-400 group-hover:text-blue-300 truncate leading-tight mt-0.5 flex items-center gap-1">
            <span className="truncate flex-1">{result.title}</span>
            {/* I7: 低相关结果加黄色警示角标 */}
            {result._low_relevance && (
              <span title="AI 判定相关性较低，仅供参考" className="shrink-0 px-1 py-0.5 rounded text-[9px] bg-amber-500/20 text-amber-400 border border-amber-500/30">
                低相关
              </span>
            )}
          </h4>
          
          {/* 摘要（单行） */}
          {hasSnippet && !expanded && (
            <p className="mt-0.5 text-[11px] text-gray-500 line-clamp-1 leading-relaxed">
              {result.snippet}
            </p>
          )}
        </div>

        {/* 外部链接图标 */}
        <ExternalLink 
          size={11} 
          className="shrink-0 mt-1 text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity" 
        />
      </a>

      {/* 展开/收起按钮 */}
      {hasSnippet && result.snippet!.length > 60 && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="ml-6 mt-0.5 text-[10px] text-gray-500 hover:text-gray-300 flex items-center gap-0.5 transition-colors"
        >
          {expanded ? ACTION_LABELS.collapse : ACTION_LABELS.expand}
          {expanded ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
        </button>
      )}

      {/* 展开后的完整摘要 */}
      {expanded && hasSnippet && (
        <div className="ml-6 mt-1 px-2 py-1.5 rounded bg-white/[0.02] border border-white/5">
          <p className="text-[11px] text-gray-400 leading-relaxed">
            {result.snippet}
          </p>
        </div>
      )}
    </div>
  );
};

/** 流式等待时显示的思考指示器 — 旋转图标让用户知道 AI 仍在工作 */
const StreamingThinkingTimer: React.FC = () => {
  return (
    <div className="flex items-center gap-1.5 text-[11px] text-gray-400">
      <Loader2 size={12} className="animate-spin text-amber-400/70" />
      <span className="italic">AI 正在分析搜索结果...</span>
    </div>
  );
};

interface SearchResultsListProps {
  query: string;
  results: SearchResult[];
  summary?: string;
  answer?: string;
  searchType?: string;
  optimizedQuery?: string;
  crawledPages?: number;
  suggestions?: string[];
  savedImages?: SavedSearchImage[];
  visionSummary?: string;
  rawSearchData?: any;
  warning?: string;
  searchLogs?: any[];
  streamingSummary?: string;
  /** P6-1: 流式场景传 true，不完整 markdown 结构先转纯文本，防止视觉闪烁 */
  skipIncomplete?: boolean;
}

export const SearchResultsList: React.FC<SearchResultsListProps> = ({
  query,
  results = [],
  summary,
  answer,
  searchType,
  optimizedQuery,
  crawledPages,
  suggestions,
  savedImages,
  visionSummary,
  rawSearchData,
  warning,
  searchLogs,
  streamingSummary,
  skipIncomplete = false,  // P6-1: 默认 false，历史消息保留完整格式
}) => {
  const [sourcesExpanded, setSourcesExpanded] = useState(false);
  const typeLabel = searchType === 'image' ? '图片' : searchType === 'video' ? '视频' : searchType === 'news' ? '新闻' : '网页';
  const isRagSearch = searchType === 'rag' || !!answer;
  const mainContent = answer || summary;

  // 流式阶段：如果有 streamingSummary，优先使用（包含增量文本）
  // P1(v5): streamingSummary !== undefined 表示处于流式阶段
  // 当 streamingSummary === "" 表示流式已开始但 AI 摘要尚未输出文本
  // 此时应显示 "AI 正在分析" 占位符，而非隐藏整个区块
  const isStreaming = streamingSummary !== undefined;
  const displayContent = streamingSummary || mainContent;
  const isImageSearch = searchType === 'image';
  // 图片搜索：允许所有 URL；通用搜索：只允许本地路径（外部 URL 受防盗链保护）
  const hasThumbnails = results.some(r => {
    const t = r.thumbnail || r.full_image;
    if (!t) return false;
    if (isImageSearch) return true; // 图片搜索：允许外部 URL（通过 referrerpolicy 绕过防盗链）
    return t.startsWith('/assets/') || t.startsWith('data:'); // 通用搜索：只允许本地路径
  });
  const hasSavedImages = savedImages && savedImages.length > 0;
  
  // 检查是否是降级结果或错误结果
  const isFallbackResult = rawSearchData?._fallback || false;
  // P0修复：降级数据不算错误，只有真正的搜索失败才算
  const hasError = !isFallbackResult && (summary?.includes('搜索失败') || summary?.includes('不可用'));

  if (!results || results.length === 0) {
    // P0修复：降级数据（历史消息 skill_result 丢失）只显示 AI 分析，不显示"未搜索到资料"
    if (isFallbackResult && displayContent) {
      return (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <Search size={12} className="text-blue-400" />
            <span className="truncate">搜索「{optimizedQuery || query}」</span>
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-1.5 text-[11px] text-amber-300">
              <Sparkles size={14} className="text-amber-400" />
              <span className="font-semibold">AI 分析</span>
            </div>
            <div className="select-text cursor-text">
              <SummaryErrorBoundary fallback={displayContent} resetKey={displayContent}>
                {renderFormattedMessage(displayContent, skipIncomplete)}
              </SummaryErrorBoundary>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <Search size={12} className={hasError ? "text-red-400" : "text-blue-400"} />
          <span className="truncate">搜索「{optimizedQuery || query}」</span>
        </div>

        {hasError ? (
          <div className="rounded-lg border border-red-500/20 bg-red-500/5 px-3 py-2.5">
            <div className="flex items-start gap-2">
              <AlertCircle size={14} className="text-red-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-[12px] text-red-300 font-medium">搜索服务暂时不可用</p>
                <p className="text-[11px] text-red-200/70 mt-1">
                  {displayContent || '请稍后重试，或尝试简化搜索关键词'}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* P1(v5): 无结果场景同样处理流式占位符 */}
            {(isStreaming || displayContent) && (
              <div className="space-y-2">
                <div className="flex items-center gap-1.5 text-[11px] text-amber-300">
                  <Sparkles size={14} className="text-amber-400" />
                  <span className="font-semibold">{isRagSearch ? 'AI 深度分析' : 'AI 分析'}</span>
                  {/* 🔴 流式阶段持续显示思考计时器，直到流结束 */}
                  {isStreaming && <StreamingThinkingTimer />}
                </div>
                <div className="select-text cursor-text">
                  {displayContent ? (
                    <SummaryErrorBoundary fallback={displayContent} resetKey={displayContent}>
                      {renderFormattedMessage(displayContent, skipIncomplete)}
                    </SummaryErrorBoundary>
                  ) : (
                    <div className="text-[11px] text-gray-400 italic animate-pulse">
                      AI 正在分析搜索结果...
                    </div>
                  )}
                </div>
              </div>
            )}
            <div className="text-[12px] text-gray-500 px-1 py-2">
              <p>很抱歉，未能搜索到相关资料。您可以尝试调整搜索关键词。</p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                <span className="text-[11px] text-gray-500">建议：</span>
                <button className="text-[11px] text-blue-400 hover:text-blue-300">
                  尝试更简短的关键词
                </button>
                <span className="text-gray-600">·</span>
                <button className="text-[11px] text-blue-400 hover:text-blue-300">
                  检查拼写
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-[11px] text-gray-500">
        <Search size={12} className={isRagSearch ? "text-purple-400 shrink-0" : "text-blue-400 shrink-0"} />
        <span className="truncate">
          {optimizedQuery && optimizedQuery !== query ? (
            <>搜索「{optimizedQuery}」</>
          ) : (
            <>搜索「{query}」</>
          )}
        </span>
        <span className="text-gray-600">·</span>
        <span>{results.length} 条{typeLabel}结果</span>

        {/* 显示高清图片数量 */}
        {hasSavedImages && (
          <>
            <span className="text-gray-600">·</span>
            <span className="text-purple-400/70 flex items-center gap-1">
              <ImageIcon size={12} />
              {savedImages!.length} 张高清图
            </span>
          </>
        )}

        {crawledPages !== undefined && crawledPages > 0 && (
          <>
            <span className="text-gray-600">·</span>
            <span className="text-green-400/70">已深度阅读 {crawledPages} 页</span>
          </>
        )}
        {isRagSearch && (
          <>
            <span className="text-gray-600">·</span>
            <span className="text-purple-400/70">RAG 增强</span>
          </>
        )}
      </div>

      {/* 搜索警告提示（部分失败等场景） */}
      {warning && (
        <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 px-3 py-2">
          <div className="flex items-start gap-2">
            <AlertCircle size={14} className="text-amber-400 mt-0.5 shrink-0" />
            <p className="text-[12px] text-amber-200/80">{warning}</p>
          </div>
        </div>
      )}

      {/* P1(v5): 流式阶段即使没有内容也显示区块 + loading 占位符
           isStreaming=true 时 builder.text 可能是 ""（流式开始但 AI 摘要第一字未到）
           历史消息: isStreaming=false，只在 displayContent 非空时显示 */}
      {(isStreaming || displayContent) && (
        <div className="space-y-2">
          <div className="flex items-center gap-1.5 text-[11px] text-amber-300">
            <Sparkles size={14} className="text-amber-400" />
            <span className="font-semibold">{isRagSearch ? 'AI 深度分析' : 'AI 分析'}</span>
            {/* 🔴 流式阶段持续显示思考计时器，直到流结束 */}
            {isStreaming && <StreamingThinkingTimer />}
          </div>
          <div className="select-text cursor-text">
            {displayContent ? (
              <SummaryErrorBoundary fallback={displayContent} resetKey={displayContent}>
                {renderFormattedMessage(displayContent, skipIncomplete)}
              </SummaryErrorBoundary>
            ) : (
              <div className="text-[11px] text-gray-400 italic animate-pulse">
                AI 正在分析搜索结果...
              </div>
            )}
          </div>
        </div>
      )}

      {/* 图片画廊 - 支持缩略图和保存的高清图片 */}
      {(isImageSearch || hasThumbnails || hasSavedImages) && (
        <ImageGallery results={results} savedImages={savedImages} searchType={searchType} />
      )}

      {/* 视觉总结：AI分析参考图的设计要点 */}
      {visionSummary && (
        <div className="space-y-1.5">
          <div className="flex items-center gap-1.5 text-[11px] text-emerald-300">
            <Sparkles size={14} className="text-emerald-400" />
            <span className="font-semibold">参考图分析</span>
          </div>
          <div className="text-[12px] text-gray-300 leading-relaxed">
            {renderFormattedMessage(visionSummary, false)}
          </div>
        </div>
      )}

      <div className="border-t border-white/5 pt-3">
        <button
          onClick={() => setSourcesExpanded(!sourcesExpanded)}
          className="flex items-center gap-1.5 text-[11px] text-gray-400 hover:text-gray-300 transition-colors w-full"
        >
          {sourcesExpanded ? (
            <ChevronDown size={12} className="text-gray-500" />
          ) : (
            <ChevronRight size={12} className="text-gray-500" />
          )}
          <Globe size={12} className="text-gray-500" />
          <span>搜索来源</span>
          <span className="text-gray-600">({results.length})</span>
        </button>

        {sourcesExpanded && (
          <div className="mt-2 space-y-1 max-h-[360px] overflow-y-auto scrollbar-thin">
            {results.map((result, index) => (
              <SearchResultCard
                key={`${result.url}-${index}`}
                result={result}
                index={index}
              />
            ))}
          </div>
        )}
      </div>

      {suggestions && suggestions.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-1">
          {suggestions.slice(0, 5).map((s, i) => (
            <span
              key={i}
              className="px-2 py-0.5 rounded-full bg-white/[0.04] text-[10px] text-gray-500 hover:text-gray-300 hover:bg-white/[0.08] cursor-pointer transition-colors"
            >
              {s}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export { ImageGallery } from './components/ImageGallery';
